import React, { Component } from "react";
import { connect } from "react-redux";

// Components
import DropzoneWrapper from "./DropzoneWrapper";
import ErrorWrap from "./error-success/ErrorWrap";

// State
import { userTokenUpdate, userLogout } from "../../store/actions/user";

// Utils
import ChunkUploader from "../../utils/ChunkUploader";

/*
 * Prop notes
 * - loading={bool}         // If this component should begin it's upload process
 * - clear={bool}           // If this component should clear it's state.  Usually happens once there is a successfull upload by parent
 * - required={false}       // If the image is actually required
 * - textLarge={string}     // Larger text to show on the side title bar
 * - textSmall={string}     // Smaller text to show on the side title bar
 * - type={string}          // Type of asset component will be uploading (mobile/full/etc)
 * - uploadAssetType={string} // Type of asset we are uploading.  This is passed to the API to ensure upload is uploaded to correct location
 * - addPromise={promise}   // Return the promise of the upload process to the parent
 * - initialImages={array}  // Initial images for this component
 * - multiple={false}       // Allow multiple images to be selected and uploaded at once.
 * - allowMultiple={false}  // Allow multiple images to be within the uploader.  If false, only one image can be present at any time.
 * - imageProperty={string} // The object property name to use for the image (object.img, use the imageProperty 'img')
 * - assetIndex={int}       // The index of the asset the parent references.  Promos is a good example of it's use.  promo[0], promo[1], promo[2]
 * - error={string}         // A specific error passed back by the parent (usually if the record or some data is crap on the main parent's update)
 * - toolbarTypes={{
 *       alt: {
 *           update: (alt, index, assetIndex) => this.updateAlt(alt, index, assetIndex),
 *           value: !!promos[0] ? promos[0].alt : ''
 *       },
 *       link: {
 *           update: (href, index, assetIndex) => this.updateLink(href, index, assetIndex),
 *           value: !!promos[0] ? promos[0].href : ''
 *       }
 *   }}
 */

class ImageUploader extends Component {
	constructor(props) {
		super(props);
		this.state = {
			img: [],
			error: "",
			token: "",
			uploadPromises: []
		};

		this.updateAlt = this.updateAlt.bind(this);
		this.updateLink = this.updateLink.bind(this);
	}

	componentWillReceiveProps(nextProps) {
		// If the submit button was pressed
		if (nextProps.loading === true && this.props.loading !== nextProps.loading) {
			this.uploadStart();
		}

		// Loading has ended (error or something in parent)
		if (nextProps.loading === false && this.props.loading !== nextProps.loading) {
			this.setState({
				img: this.state.img.map(img => {
					let updatedImg = img;
					updatedImg.loading = false;
					updatedImg.completed = 0;
					updatedImg.message = "";
					return updatedImg;
				})
			});
		}

		// If submit was completed, clear the state
		if (nextProps.clear && this.props.clear !== nextProps.clear) {
			this.clearState();
		}

		if (!!nextProps.error && nextProps.error !== "") {
			this.setState({
				error: nextProps.error
			});
		}
	}

	clearState() {
		this.setState({
			img: [],
			error: "",
			token: ""
		});
	}

	handleImgUpdate(name, file) {
		this.setState({
			img: this.props.allowMultiple ? [...this.state.img, file] : [file]
		});
	}

	handleImgRemoval(files) {
		this.setState({
			img: files
		});
	}

	handleUploadProgress(imageIndex, percentage = "", message = "") {
		this.setState({
			img: this.state.img.map((item, index) => {
				if (index !== imageIndex) return item;

				let imgItem = item;
				imgItem.completed = percentage;
				imgItem.message = !message ? percentage + "%" : message;

				return imgItem;
			})
		});
	}

	updateUploadToken(token) {
		this.setState({
			token
		});
	}

	uploadStart() {
		// Reset all errors, etc
		this.setState(
			{
				error: ""
			},
			() => {
				// Begin upload process
				this.props.addPromise(
					new Promise((resolve, reject) => {
						// Get data from state
						const { img, uploadPromises } = this.state;

						// Image is null and not required, do nothing
						if (!img.length && !this.props.required) {
							return resolve(null);
						}

						// Image is null and IS required
						if (!img.length) {
							const err = "Unable to determine the file to upload";
							this.setState({
								error: err
							});
							return reject(err);
						}

						// Loop through all images
						img.forEach((imgItem, imageIndex) => {
							// ===================================
							//  Add a promise to the parent
							// ===================================
							uploadPromises.push(
								new Promise((resolve, reject) => {
									// Set the file to loading
									this.setState(
										{
											img: this.state.img.map((item, index) => {
												if (index !== imageIndex) return item;
												imgItem.loading = true;
												return imgItem;
											})
										},
										() => {
											// Initialize chunk uploader
											let uploader = new ChunkUploader();

											// Begin upload
											return uploader
												.uploadFile(this.props.uploadAssetType, imgItem, this.props.user.token, this.updateUploadToken.bind(this), imageIndex, this.handleUploadProgress.bind(this))
												.then(res => {
													// console.log('success child', res)

													// Send back a resolved object with any manually set info (alt, link, etc)
													return resolve({
														type: !!this.props.type ? this.props.type : null,
														results: {
															...res,
															link: imgItem.link,
															alt: imgItem.alt
														}
													});
												})
												.catch(err => {
													console.log(err);

													// Check user token
													if (err.obj.data.status === 403) {
														this.props.userLogout("admin");
													}

													this.setState({
														error: err.error
													});
													return reject(err.error);
												});
										}
									);
								})
							);
						});

						// console.log(uploadPromises.length, uploadPromises)

						return Promise.all(uploadPromises)
							.then(res => {
								// console.log('imageUploader all', res)
								this.setState({
									uploadPromises: []
								});
								return resolve(res);
							})
							.catch(err => {
								this.setState({
									uploadPromises: [],
									error: err
								});
								return reject(err);
							});
					})
				);
			}
		);
	}

	updateAlt(alt, index, isCurrent, assetIndex) {
		if (isCurrent) {
			this.props.toolbarTypes.alt.update(alt, index, isCurrent, assetIndex);
			return;
		}

		this.setState({
			img: this.state.img.map((img, imgIndex) => {
				if (imgIndex !== index) return img;
				img.alt = alt;
				return img;
			})
		});
	}

	updateLink(link, index, isCurrent, assetIndex) {
		if (isCurrent) {
			this.props.toolbarTypes.link.update(link, index, isCurrent, assetIndex);
			return;
		}

		this.setState({
			img: this.state.img.map((img, imgIndex) => {
				if (imgIndex !== index) return img;
				img.link = link;
				return img;
			})
		});
	}

	render() {
		const { img, error } = this.state;
		const { textLarge, textSmall } = this.props;

		// Set up any "interceptors".
		// These will ensure any new images keep their data together with the file
		let toolbarTypesInterceptors = this.props.toolbarTypes;

		if (!!toolbarTypesInterceptors && !!toolbarTypesInterceptors.alt) {
			toolbarTypesInterceptors.alt.updateAlt = this.updateAlt;
		}
		if (!!toolbarTypesInterceptors && !!toolbarTypesInterceptors.link) {
			toolbarTypesInterceptors.link.updateLink = this.updateLink;
		}

		return (
			<div className="image-drop">
				{!!error && <ErrorWrap animate={1}>{error}</ErrorWrap>}
				<div className="image-upload-layout">
					<div className={"text-section" + (!!textLarge || !!textSmall ? " active" : "")}>
						<div>
							{!!textLarge && <span>{textLarge}</span>}
							{!!textSmall && <span className="small-txt">{textSmall}</span>}
						</div>
					</div>
					<div className={"image-upload-section" + (!!textLarge || !!textSmall ? " image-upload--with-text" : "")}>
						<DropzoneWrapper img={img} handleImgUpdate={this.handleImgUpdate.bind(this)} handleImgRemoval={this.handleImgRemoval.bind(this)} toolbarTypesInterceptors={toolbarTypesInterceptors} {...this.props} />
					</div>
				</div>
			</div>
		);
	}
}

const mapStateToProps = state => {
	return {
		user: state.user.admin
	};
};

export default connect(
	mapStateToProps,
	{ userTokenUpdate, userLogout }
)(ImageUploader);



// WEBPACK FOOTER //
// ./src/components/utils/ImageUploader.js